import { useState, useEffect } from 'react';
import api, { downloadFile, downloadFilePost } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, FileText, ScrollText } from 'lucide-react';

export default function LettersPage() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    api.get('/letters/history')
      .then(res => setLetters(res.data))
      .catch(() => toast.error('Failed to load letter history'))
      .finally(() => setLoading(false));
  }, []);

  const handleRedownload = async (letter) => {
    setDownloading(letter.id);
    try {
      if (letter.type === 'offer') {
        await downloadFile(
          `/letters/offer/${letter.employee_user_id}`,
          `offer_letter_${letter.employee_emp_id}.pdf`
        );
      } else {
        await downloadFilePost(
          `/letters/relieving/${letter.employee_user_id}`,
          { last_working_date: letter.last_working_date || '', notes: letter.notes || '' },
          `relieving_letter_${letter.employee_emp_id}.pdf`
        );
      }
      toast.success('Letter downloaded');
    } catch {
      toast.error('Download failed - employee data may no longer be available');
    } finally {
      setDownloading('');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div data-testid="letters-page">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Letter History</h2>
        <p className="text-sm text-muted-foreground font-body mt-1">Track all generated offer and relieving letters</p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : letters.length === 0 ? (
        <div className="border border-border bg-card rounded-sm p-12 text-center">
          <ScrollText className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">No letters generated yet</p>
          <p className="text-xs text-muted-foreground mt-1">Generate offer or relieving letters from the Employees page</p>
        </div>
      ) : (
        <div className="border border-border bg-card rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">ID</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Department</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Generated By</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Details</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {letters.map(letter => (
                <TableRow key={letter.id} data-testid={`letter-row-${letter.id}`}>
                  <TableCell className="font-mono text-xs">{formatDate(letter.generated_at)}</TableCell>
                  <TableCell>
                    <Badge className={
                      letter.type === 'offer'
                        ? 'bg-blue-50 text-[#002FA7] border-blue-200'
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                    }>
                      <FileText className="w-3 h-3 mr-1" strokeWidth={1.5} />
                      {letter.type === 'offer' ? 'Offer' : 'Relieving'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{letter.employee_name}</TableCell>
                  <TableCell className="font-mono text-xs">{letter.employee_emp_id}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{letter.department}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{letter.generated_by}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {letter.type === 'relieving' && letter.last_working_date
                      ? `LWD: ${letter.last_working_date}`
                      : letter.designation
                    }
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRedownload(letter)}
                      disabled={downloading === letter.id}
                      data-testid={`redownload-letter-${letter.id}`}
                      className="text-[#002FA7] hover:text-[#002585]"
                    >
                      <Download className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      {downloading === letter.id ? '...' : 'Download'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
